import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { eventService } from '../../../services';
import ResponsePageWrapper from '../../../components/layouts/ResponsePageWrapper';
import useAuth from '../../../hooks/useAuth';
import Button from '../../../components/misc/Button';
import { LoginPageState, SuccessPageState } from '../../../types/navigation';
import { PersonalEvent as Event, EventParticipationAnswer } from '../../../types/event';
import { getEventAttendees, getPollResponses } from '../../../utils/eventUtils';
import { getUserId } from '../../../services/httpService';
import GenericInput from '../../../components/forms/GenericInput';
import CustomFieldsForm from '../../../components/forms/CustomFieldsForm';
import { CustomFieldResponses, InviteType } from '../../../types/invites';
import { formatCustomFieldResponsesFromCustomFields } from '../../../utils/formatUtils';
import EventOverview from '../../../components/overviews/EventOverview';
import useMagicLogin from '../../../hooks/useMagicLogin';
import useDeleteParams from '../../../hooks/useDeleteParams';
import AttendanceComponent from '../../../components/misc/AttendanceComponent';

function EventRespondPage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const eventId = params.get('id');
  const [event, setEvent] = useState<Event>();
  const [selectedAnswer, setSelectedAnswer] = useState<EventParticipationAnswer | null>(null);
  const [remark, setRemark] = useState<string>('');
  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>([]);

  if (useMagicLogin()) {
    return <></>;
  }

  useDeleteParams('link');

  useEffect(() => {
    if (!eventId) {
      toast.error(t('toast.error.general.invalidInvite'));
      setTimeout(() => navigate('/'), 3000);
      return;
    }

    if (!auth) return;

    eventService
      .getEvent(+eventId)
      .then((e) => setEvent(e))
      .catch(() => {
        toast.error(t('toast.error.general.inviteNotFound'));
        setTimeout(() => navigate('/'), 3000);
      });
  }, [JSON.stringify(auth)]);

  useEffect(() => {
    if (!auth) return;

    try {
      const userId = getUserId();
      if (!userId || !event) return;

      if (event.isDatePicker) {
        navigate(`/date-picker?id=${eventId}`);
      }

      const userAnswer = event.participants.find((participant) => participant.id === userId);
      if (userAnswer) {
        setSelectedAnswer(userAnswer.answer || EventParticipationAnswer.PENDING);
        setRemark(userAnswer.description || '');
      }
      const responses = getPollResponses(userId, event!.customFields);
      setCustomFieldResponses(formatCustomFieldResponsesFromCustomFields(event!.customFields, responses));
    } catch (error) {
      console.error(error);
    }
  }, [event]);

  const validate = () => {
    if (!selectedAnswer || selectedAnswer === EventParticipationAnswer.PENDING) {
      toast.error(t('toast.error.field.noAnswer'));
      return false;
    }
    if (maximumAttendeesReached && selectedAnswer === EventParticipationAnswer.YES) {
      toast.error(t('toast.error.events.maximumAttendeesReached'));
      return false;
    }

    if (event!.customFields.length > 0) {
      return event!.customFields.every((field) => {
        if (field.mandatory && !customFieldResponses[field.customFieldId]) {
          toast.error(t('toast.error.field.mandatoryStar'));
          return false;
        }
        return true;
      });
    }

    return true;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validate()) return;

    await eventService.respondToEvent(eventId!, selectedAnswer!, remark);

    if (event!.customFields.length > 0) {
      await eventService.respondToEventPollBulk(eventId!, customFieldResponses);
    }

    const state: SuccessPageState = { translationKey: 'page.events.invite.message.success' };
    navigate('/success', { state });
  };

  const handleLogin = async () => {
    const state: LoginPageState = { inviteType: InviteType.EVENT, eventId: eventId!, hideBackButton: true };
    navigate('/login', { state });
  };

  if (!auth)
    return (
      <ResponsePageWrapper handleLogin={handleLogin}>
        <div className="flex flex-col gap-4">
          <h1 className="text-3xl font-semibold">{t('page.events.invite.message.logIn')}</h1>
          <Button onClick={handleLogin} className="self-start" variant="primary">
            {t('page.shared.logInHere')}
          </Button>
        </div>
      </ResponsePageWrapper>
    );

  if (!event || !eventId) return <></>;

  const { maximumAttendees, deadline } = event;

  const numberOfAttendees = getEventAttendees(event);
  const deadlineIsPassed = deadline ? new Date() > new Date(deadline) : false;
  const maximumAttendeesReached = maximumAttendees ? numberOfAttendees >= maximumAttendees : false;
  const remarkPlaceholder = selectedAnswer
    ? `${t(`page.events.invite.placeholder.eg`)} ${t(`page.events.invite.placeholder.${selectedAnswer}.reason${Math.floor(Math.random() * 10) + 1}`)}`
    : '';

  return (
    <ResponsePageWrapper handleLogin={handleLogin}>
      {event && (
        <ResponsePageWrapper.SidePanel>
          <EventOverview event={event} />
        </ResponsePageWrapper.SidePanel>
      )}

      {!deadlineIsPassed && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-medium">{t('page.events.invite.areYouAttending')}</h2>
          <div className="flex gap-4">
            <AttendanceComponent
              selectedAnswer={selectedAnswer}
              setSelectedAnswer={setSelectedAnswer}
              disableYes={maximumAttendeesReached}
            />
          </div>
          <div>
            <GenericInput
              label={t('page.events.invite.note')}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder={selectedAnswer !== EventParticipationAnswer.PENDING ? remarkPlaceholder : ''}
              textarea
            />
          </div>

          {event && event.customFields.length > 0 && (
            <>
              <div className="text-lg">{t('page.events.invite.polls.title')}</div>
              <CustomFieldsForm
                fields={event.customFields}
                response={customFieldResponses}
                setResponse={setCustomFieldResponses}
              />
            </>
          )}

          <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
            <Button className="underline" onClick={() => navigate('/')}>
              {t('general.cancel')}
            </Button>
            <Button variant="primary" className="px-[32px]" onClick={handleSubmit}>
              {t('general.save')}
            </Button>
          </div>
        </div>
      )}
    </ResponsePageWrapper>
  );
}

export default EventRespondPage;
